import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Save, RotateCcw, Info, Settings as SettingsIcon, Layers, ReceiptText, Calendar } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Hash, ArrowRight, History } from "lucide-react";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

function applyFormat(fmt, num, date) {
  const d = new Date(date);
  return fmt
    .replace("{num}", String(num).padStart(3, "0"))
    .replace("{romawi}", ROMAN[d.getMonth()])
    .replace("{bulan}", String(d.getMonth() + 1).padStart(2, "0"))
    .replace("{tahun}", d.getFullYear());
}

const RESET_TONE = {
  bulanan: "bg-sky-100 text-sky-800",
  tahunan: "bg-emerald-100 text-emerald-800",
  manual: "bg-amber-100 text-amber-800",
};

export default function PengaturanPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [nextInput, setNextInput] = useState({ SPH: "", Invoice: "" });
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        api.get("/settings/numbering"),
        api.get("/settings/numbering/status"),
      ]);
      setSettings(s.data);
      setStatus(st.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const reloadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/numbering/status");
      setStatus(data);
    } catch (e) {
      // Status panel is informational; surface to console only.
      // eslint-disable-next-line no-console
      console.error("Failed to reload numbering status:", formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function applyNext(jenis) {
    const raw = nextInput[jenis];
    const n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 1) {
      toast.error("Masukkan nomor berikutnya (bilangan bulat ≥ 1)");
      return;
    }
    try {
      await api.post("/settings/numbering/set-next", { jenis, next_number: n });
      toast.success(`Nomor berikutnya ${jenis} di-set ke ${n}`);
      setNextInput({ ...nextInput, [jenis]: "" });
      reloadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function save() {
    setLoading(true);
    try {
      const { data } = await api.put("/settings/numbering", settings);
      setSettings(data);
      toast.success("Pengaturan disimpan");
      reloadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  async function resetCounters() {
    try {
      await api.post("/settings/numbering/reset");
      toast.success("Nomor urut direset");
      reloadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (!settings) return <div className="text-sm text-ink-500">Memuat…</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="pengaturan-page">
      <PageHeader
        overline="Administrasi"
        title="Pengaturan Penomoran Surat"
        description="Atur format nomor, nomor awal, dan mode reset untuk setiap jenis surat."
        icon={SettingsIcon}
        tone="neutral"
      />

      {/* ====== Mulai Dari Nomor (lanjutkan dari penomoran lama) ====== */}
      <Card className="relative overflow-hidden border-border shadow-none" data-testid="set-next-card">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-brand via-sky-400 to-amber-400" />
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-gradient-to-br from-brand to-brand-700 text-white shadow-sm">
              <Hash className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="overline text-brand-700">Lanjutkan dari nomor lama</p>
              <h2 className="font-heading text-xl font-bold tracking-tight text-ink-900">
                Mulai dari nomor berikutnya
              </h2>
              <p className="mt-1 text-sm text-ink-500">
                Jika Anda sudah menerbitkan surat secara manual (misal sampai nomor 7),
                set nomor berikutnya ke <span className="font-medium text-ink-700">8</span> agar
                aplikasi melanjutkan urutan tanpa bentrok. Berlaku untuk SPH dan Invoice masing-masing.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[
              { key: "SPH", label: "SPH", tint: "sky", borderColor: "border-sky-200", chipBg: "bg-sky-500", soft: "from-sky-50 to-white", text: "text-sky-900", overline: "text-sky-700", focusRing: "focus-visible:ring-sky-400", Icon: Layers },
              { key: "Invoice", label: "Invoice", tint: "amber", borderColor: "border-amber-200", chipBg: "bg-amber-500", soft: "from-amber-50 to-white", text: "text-amber-900", overline: "text-amber-700", focusRing: "focus-visible:ring-amber-400", Icon: ReceiptText },
            ].map((cfg) => {
              const st = status?.[cfg.key];
              const Icon = cfg.Icon;
              return (
                <div
                  key={cfg.key}
                  className={`rounded-lg border ${cfg.borderColor} bg-gradient-to-br ${cfg.soft} p-4`}
                  data-testid={`set-next-${cfg.key.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`grid h-9 w-9 place-items-center rounded-md ${cfg.chipBg} text-white shadow-sm`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className={`overline ${cfg.overline}`}>{cfg.label}</p>
                      <p className={`font-heading text-lg font-bold ${cfg.text}`}>
                        Nomor berikutnya: {st ? st.next_number : "…"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-ink-500 sm:grid-cols-2">
                    <div className="rounded-md bg-white/70 px-2 py-1.5">
                      Terakhir terbit: <span className="font-mono text-ink-700">{st?.last_issued ?? "—"}</span>
                    </div>
                    <div className="rounded-md bg-white/70 px-2 py-1.5">
                      Terbit di periode: <span className="font-mono text-ink-700">{st?.total_issued_in_period ?? 0}</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Set nomor berikutnya</Label>
                      <Input
                        type="number"
                        min="1"
                        placeholder={st ? String(st.next_number) : "1"}
                        value={nextInput[cfg.key]}
                        onChange={(e) => setNextInput({ ...nextInput, [cfg.key]: e.target.value })}
                        className={`${cfg.borderColor.replace("border-", "border-")} ${cfg.focusRing}`}
                        data-testid={`input-next-${cfg.key.toLowerCase()}`}
                      />
                    </div>
                    <Button
                      onClick={() => applyNext(cfg.key)}
                      className={`${cfg.chipBg} text-white hover:opacity-90`}
                      data-testid={`btn-apply-next-${cfg.key.toLowerCase()}`}
                    >
                      Terapkan <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-md border border-dashed border-border bg-secondary/40 p-3 text-xs text-ink-500">
            <History className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Perubahan nomor berikutnya tidak mempengaruhi surat yang sudah terbit dan akan tercatat di
              Audit Log dengan aksi <span className="font-mono text-ink-700">set_next_number</span>.
            </span>
          </div>
        </div>
      </Card>

      {/* Card: Mode Reset */}
      <Card className="overflow-hidden border-border shadow-none">
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className={`grid h-11 w-11 place-items-center rounded-md ${RESET_TONE[settings.reset_mode] || "bg-slate-100 text-slate-700"}`}>
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <p className="overline">Mode reset counter</p>
              <p className="mt-1 text-sm text-ink-700">
                Nomor urut akan dimulai dari awal pada periode <span className="font-medium">{settings.reset_mode}</span>.
              </p>
            </div>
          </div>
          <div className="min-w-[200px]">
            <Select value={settings.reset_mode} onValueChange={(v) => setSettings({ ...settings, reset_mode: v })}>
              <SelectTrigger data-testid="input-reset-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bulanan">Bulanan</SelectItem>
                <SelectItem value="tahunan">Tahunan</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Two colorful columns: SPH & Invoice */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* SPH */}
        <Card className="relative overflow-hidden border-sky-200 bg-gradient-to-br from-sky-50 via-white to-white shadow-none">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-500 via-sky-400 to-sky-300" />
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-sky-500 text-white shadow-sm">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <p className="overline text-sky-700">Surat Penawaran Harga</p>
                <h2 className="font-heading text-xl font-bold tracking-tight text-sky-900">SPH</h2>
              </div>
            </div>

            <div>
              <Label>Format Nomor SPH</Label>
              <Input
                value={settings.sph_format}
                onChange={(e) => setSettings({ ...settings, sph_format: e.target.value })}
                data-testid="input-sph-format"
                className="font-mono border-sky-200 focus-visible:ring-sky-400"
              />
            </div>

            <div>
              <Label>Nomor Awal SPH</Label>
              <Input
                type="number" min="1"
                value={settings.sph_start}
                onChange={(e) => setSettings({ ...settings, sph_start: Number(e.target.value) })}
                data-testid="input-sph-start"
                className="border-sky-200 focus-visible:ring-sky-400"
              />
            </div>

            <div className="rounded-md border border-sky-200 bg-white p-3">
              <p className="overline text-sky-700">Preview</p>
              <p
                className="mt-1 font-mono text-lg font-semibold tracking-tight text-sky-900"
                data-testid="preview-sph"
              >
                {applyFormat(settings.sph_format, settings.sph_start, today)}
              </p>
            </div>
          </div>
        </Card>

        {/* Invoice */}
        <Card className="relative overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white shadow-none">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-300" />
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-amber-500 text-white shadow-sm">
                <ReceiptText className="h-5 w-5" />
              </div>
              <div>
                <p className="overline text-amber-700">Invoice / Tagihan</p>
                <h2 className="font-heading text-xl font-bold tracking-tight text-amber-900">Invoice</h2>
              </div>
            </div>

            <div>
              <Label>Format Nomor Invoice</Label>
              <Input
                value={settings.invoice_format}
                onChange={(e) => setSettings({ ...settings, invoice_format: e.target.value })}
                data-testid="input-invoice-format"
                className="font-mono border-amber-200 focus-visible:ring-amber-400"
              />
            </div>

            <div>
              <Label>Nomor Awal Invoice</Label>
              <Input
                type="number" min="1"
                value={settings.invoice_start}
                onChange={(e) => setSettings({ ...settings, invoice_start: Number(e.target.value) })}
                data-testid="input-invoice-start"
                className="border-amber-200 focus-visible:ring-amber-400"
              />
            </div>

            <div className="rounded-md border border-amber-200 bg-white p-3">
              <p className="overline text-amber-700">Preview</p>
              <p
                className="mt-1 font-mono text-lg font-semibold tracking-tight text-amber-900"
                data-testid="preview-invoice"
              >
                {applyFormat(settings.invoice_format, settings.invoice_start, today)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Variable info */}
      <div className="rounded-md border border-border bg-gradient-to-r from-slate-50 to-white p-4">
        <div className="flex items-start gap-2 text-xs text-ink-500">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Variabel format yang tersedia:{" "}
            <span className="font-mono text-ink-700">{"{num}"}</span> (nomor urut 3 digit),{" "}
            <span className="font-mono text-ink-700">{"{romawi}"}</span> (bulan romawi),{" "}
            <span className="font-mono text-ink-700">{"{bulan}"}</span> (bulan angka 01–12),{" "}
            <span className="font-mono text-ink-700">{"{tahun}"}</span> (4 digit). Contoh:{" "}
            <span className="font-mono text-ink-700">SPH-HPM/001/I/2026</span>.
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" data-testid="btn-reset-counters">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Nomor Urut
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset semua counter?</AlertDialogTitle>
              <AlertDialogDescription>
                Nomor urut SPH dan Invoice akan kembali dari awal pada penerbitan berikutnya. Surat yang sudah terbit tidak terpengaruh.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction onClick={resetCounters} data-testid="confirm-reset-counters">Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button onClick={save} disabled={loading} className="bg-brand text-white hover:bg-brand-600" data-testid="btn-save-settings">
          <Save className="mr-2 h-4 w-4" /> {loading ? "Menyimpan…" : "Simpan Pengaturan"}
        </Button>
      </div>
    </div>
  );
}
